import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/payments/methods/slice";
import { PaymentProvider } from "store/payments/providers/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import FilterTabs from "components/FilterTabs";

import classNames from "classnames";
import { ImageUpload } from "components/imageUpload";

interface PaymentMethodsMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly: boolean;
}

const MethodsMainTab: React.FC<PaymentMethodsMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const canEdit = !isReadOnly;
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store["payment-methods"].payload);
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );

  const handleProviders = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentProvidersList}?limit=500&offset=0`,
      );
      const array =
        result.data.items?.map((el: PaymentProvider) => ({
          value: el.id,
          label: el.title,
        })) ?? [];
      setOptions(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    handleProviders();
  }, []);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };
  return (
    <div className="bonus-form-content">
      <div className="bonus-form-box">
        <div className="bonus-form-box--item">
          <DefaultDropdown
            value={payload?.providerId}
            placeholder={t("provider")}
            label={t("provider")}
            options={options}
            disabled={!canEdit || options.length <= 0}
            onSelect={(e) => {
              handleData("providerId", e);
              clearFieldError("providerId");
            }}
            hasError={Boolean(fieldErrors?.["providerId"])}
          />
        </div>

        <div className="bonus-form-box--item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="loyalty-level-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={!canEdit}
          />
        </div>

        <div className="bonus-form-box--item">
          <InnerInput
            value={payload?.code ?? ""}
            placeholder={t("code")}
            label={t("code")}
            type="text"
            id="loyalty-level-code"
            onChange={(e) => {
              handleData("code", e);
              clearFieldError("code");
            }}
            hasError={Boolean(fieldErrors?.["code"])}
            disabled={!canEdit}
          />
        </div>

        <div className="bonus-form-box--item type-currency-row">
          <div className="type-block">
            <FilterTabs
              value={payload?.transactionType}
              label={t("transactionType")}
              options={[
                { value: "deposit", label: t("deposit") },
                { value: "withdrawal", label: t("withdrawal") },
              ]}
              onSelect={(e) => {
                handleData("transactionType", e);
                clearFieldError("transactionType");
              }}
              hasError={Boolean(fieldErrors?.["transactionType"])}
              disabled={!canEdit}
            />
          </div>

          <div className="currency-block">
            <FilterTabs
              value={payload?.currencyType ?? "fiat"}
              label={t("currencyType")}
              options={[
                { value: "fiat", label: "Fiat" },
                { value: "crypto", label: "Crypto" },
              ]}
              onSelect={(e) => {
                handleData("currencyType", e);
                clearFieldError("currencyType");
              }}
              disabled={!canEdit}
            />
          </div>
        </div>

        <div className="bonus-form-box--item">
          <InnerInput
            maxCodePoints={2500}
            value={payload?.description ?? ""}
            placeholder={t("enterText")}
            label={t("description")}
            type="textarea"
            id="loyalty-level-description"
            onChange={(e) => {
              handleData("description", e);
              clearFieldError("description");
            }}
            hasError={Boolean(fieldErrors?.["description"])}
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="bonus-form-box">
        <div className="game-form-img game-form-img--game center">
          <div
            className={classNames("game-item", {
              "has-error": fieldErrors?.["file"],
            })}
          >
            <ImageUpload
              image={payload?.image}
              size="large"
              onAddImage={(file) => {
                handleData("image", "", file);
                clearFieldError("file");
              }}
              isActive={payload?.isActive}
              disabled={!canEdit}
            />
          </div>

          <div className="game-form-switcher game-form-switch--row">
            <SwitchBtn
              activeTxt={t("active")}
              notActiveTxt={t("hidden")}
              isActive={!!payload?.isActive}
              handleClick={() => {
                handleData(
                  "isActive",
                  payload?.isActive ? !payload?.isActive : true,
                );
                clearFieldError("isActive");
              }}
              hasError={Boolean(fieldErrors?.["isActive"])}
              disabled={!canEdit}
            />
            <SwitchBtn
              disabled={!canEdit}
              activeTxt={t("test")}
              isActive={!!payload?.isTest}
              isRed
              handleClick={() => {
                handleData("isTest", !payload?.isTest);
                clearFieldError("isTest");
              }}
              hasError={Boolean(fieldErrors?.["isTest"])}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodsMainTab;
